import React, { useState, useEffect } from "react";
import {
  Box,
  Title,
  Text,
  Stack,
  Group,
  Card,
  ActionIcon,
  Anchor,
  Button,
  Drawer,
  ScrollArea,
} from "@mantine/core";
import { L2KBadge } from "../components/ui/Typography";
import { useDisclosure } from "@mantine/hooks";
import { IconExternalLink, IconHome, IconBook } from "@tabler/icons-react";

// Import separated data
import { danceStyles } from "../data/danceStylesData";
import { danceStyleDictionaryEntries } from "../data/danceStyleDictionaryEntries";

const multimediaReferences = [
  {
    id: 1,
    type: "internal_video",
    title: "Basic Step Tutorial",
    url: "/videos/basic-step",
  },
  {
    id: 2,
    type: "internal_image",
    title: "Hand Position Guide",
    url: "/images/hand-position.jpg",
  },
  {
    id: 3,
    type: "external_video",
    title: "YouTube: Connection Basics",
    url: "https://youtube.com/watch?v=example",
  },
  {
    id: 4,
    type: "external_link",
    title: "History of Kizomba",
    url: "https://example.com/kizomba-history",
  },
  {
    id: 5,
    type: "podcast",
    title: "Dance Culture Podcast Ep. 12",
    url: "https://example.com/podcast",
  },
];

// Helper function to convert name to slug
const getWordSlug = (name: string) => {
  return name
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");
};

// Helper function to find term by slug
const findTermBySlug = (slug: string) => {
  return danceStyleDictionaryEntries.find(
    (term) => getWordSlug(term.name) === slug
  );
};

interface WordPageProps {
  wordSlug?: string; // The URL slug for the word
}

export default function WordPage({ wordSlug }: WordPageProps) {
  const [currentTerm, setCurrentTerm] = useState<
    (typeof danceStyleDictionaryEntries)[0] | null
  >(null);
  const [previewDrawerOpened, { toggle: togglePreviewDrawer }] =
    useDisclosure();

  type DictionaryTerm = (typeof danceStyleDictionaryEntries)[0];
  const [selectedPreviewTerm, setSelectedPreviewTerm] =
    useState<DictionaryTerm | null>(null);

  // Find the current term based on the slug
  useEffect(() => {
    let slug = wordSlug;

    // If no slug provided as prop, extract from URL
    if (!slug && typeof window !== "undefined") {
      const pathname = window.location.pathname;
      const pathParts = pathname.split("/");
      // Extract slug from /dictionary/slug-name
      if (pathParts.length >= 3 && pathParts[1] === "dictionary") {
        slug = pathParts[2];
      }
    }

    if (slug) {
      const term = findTermBySlug(slug);
      setCurrentTerm(term || null);
    } else {
      // Fallback to first term if no slug provided
      setCurrentTerm(danceStyleDictionaryEntries[0] || null);
    }
  }, [wordSlug]);

  const appShellStyles = {
    background: `
      radial-gradient(ellipse at 20% 50%, rgba(255, 152, 0, 0.15) 0%, transparent 50%),
      radial-gradient(ellipse at 80% 20%, rgba(255, 193, 7, 0.12) 0%, transparent 50%),
      radial-gradient(ellipse at 40% 80%, rgba(255, 87, 34, 0.1) 0%, transparent 50%),
      linear-gradient(135deg, #171717 0%, #1a1a1a 25%, #171717 50%, #1f1f1f 75%, #171717 100%)
    `,
    minHeight: "100vh",
    fontFamily: "'Big Shoulders Display', sans-serif",
    color: "#F0F0F0",
    padding: "40px 20px",
  };

  // Use the dictionary entries as the source of truth
  const allDictionaryTerms = danceStyleDictionaryEntries;

  const getDanceStyleName = (id: number) =>
    danceStyles.find((style) => style.id === id)?.name || "";

  const handleSeeAlsoClick = (termId: number) => {
    const term = allDictionaryTerms.find((t) => t.id === termId);
    if (term) {
      setSelectedPreviewTerm(term);
      togglePreviewDrawer();
    }
  };

  const handleDanceStyleBadgeClick = (
    styleId: number,
    event: React.MouseEvent
  ) => {
    event.stopPropagation();

    // Find a dance style dictionary entry that includes this style
    const danceStyleEntry = danceStyleDictionaryEntries.find((entry) =>
      entry.danceStyles.includes(styleId)
    );

    if (danceStyleEntry) {
      setSelectedPreviewTerm(danceStyleEntry);
      togglePreviewDrawer();
    }
  };

  const handleViewFullWord = () => {
    if (selectedPreviewTerm) {
      const slug = getWordSlug(selectedPreviewTerm.name);
      // In a real app, this would navigate to the word page
      console.log(`Navigate to /dictionary/${slug}`);
      // You could also update the URL here or call a navigation function
      window.location.href = `/dictionary/${slug}`;
    }
    togglePreviewDrawer();
  };

  // Show loading or not found state
  if (!currentTerm) {
    return (
      <Box style={appShellStyles}>
        <Stack gap="xl" style={{ maxWidth: "800px", margin: "0 auto" }}>
          <Title
            order={1}
            style={{
              fontFamily: "'Big Shoulders Display', sans-serif",
              color: "#F0F0F0",
              textAlign: "center",
            }}
          >
            {wordSlug ? `Word "${wordSlug}" not found` : "Loading..."}
          </Title>
          <Text style={{ textAlign: "center", color: "#B8B8B8" }}>
            <Anchor href="/dictionary" style={{ color: "#FF9800" }}>
              ← Back to Dictionary
            </Anchor>
          </Text>
        </Stack>
      </Box>
    );
  }

  const breadcrumbItems = [
    { title: "Home", href: "/", icon: <IconHome size={16} /> },
    { title: "Dictionary", href: "/dictionary", icon: <IconBook size={16} /> },
    { title: currentTerm.name, href: "#" },
  ].map((item, index) => (
    <Anchor
      key={index}
      href={item.href}
      style={{
        color: index === 2 ? "#FF9800" : "#D0D0D0",
        fontFamily: "'Big Shoulders Display', sans-serif",
        textDecoration: "none",
        display: "flex",
        alignItems: "center",
        gap: "4px",
        whiteSpace: "nowrap",
        flexShrink: 0,
      }}
    >
      {item.icon}
      {item.title}
    </Anchor>
  ));

  return (
    <Box style={appShellStyles}>
      <Stack gap="xl" style={{ maxWidth: "800px", margin: "0 auto" }}>
        {/* Breadcrumbs */}
        <Group
          gap="xs"
          style={{
            flexWrap: "nowrap",
            overflow: "auto",
            width: "fit-content",
          }}
        >
          {breadcrumbItems.map((item, index) => (
            <React.Fragment key={index}>
              {item}
              {index < breadcrumbItems.length - 1 && (
                <Text
                  size="sm"
                  c="#B8B8B8"
                  style={{
                    flexShrink: 0,
                    whiteSpace: "nowrap",
                  }}
                >
                  ›
                </Text>
              )}
            </React.Fragment>
          ))}
        </Group>

        {/* Main Word Header */}
        <Box>
          <Title
            order={1}
            style={{
              fontFamily: "'Big Shoulders Display', sans-serif",
              background: "linear-gradient(45deg, #FFC107, #FF9800)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              fontWeight: 700,
              fontSize: "3rem",
              lineHeight: 1.1,
              marginBottom: "16px",
            }}
          >
            {currentTerm.name}
          </Title>

          {/* Definition */}
          <Card
            padding="xl"
            radius="lg"
            style={{
              background: "rgba(255, 255, 255, 0.05)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              backdropFilter: "blur(10px)",
            }}
          >
            <Text
              size="lg"
              c="#F0F0F0"
              style={{
                fontFamily: "'Big Shoulders Display', sans-serif",
                lineHeight: 1.7,
                fontSize: "1.1rem",
              }}
            >
              {currentTerm.definition}
            </Text>
          </Card>

          {/* Dance Styles and Category Badges - Moved below definition */}
          <Group gap="sm" mb="xl" mt="12px">
            {currentTerm.danceStyles.map((styleId) => (
              <L2KBadge
                key={styleId}
                variant="subtle"
                size="lg"
                style={{
                  fontFamily: "'Big Shoulders Display', sans-serif",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  textTransform: "capitalize",
                }}
                onClick={(event) => handleDanceStyleBadgeClick(styleId, event)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor =
                    "rgba(255, 152, 0, 0.3)";
                  e.currentTarget.style.transform = "translateY(-1px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor =
                    "rgba(255, 152, 0, 0.1)";
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                {getDanceStyleName(styleId)}
              </L2KBadge>
            ))}
            <L2KBadge
              variant="muted"
              size="lg"
              style={{
                fontFamily: "'Big Shoulders Display', sans-serif",
              }}
            >
              {currentTerm.category}
            </L2KBadge>
          </Group>

          {/* Videos */}
          {currentTerm.multimedia.some(
            (id) =>
              multimediaReferences.find((ref) => ref.id === id)?.type ===
              "internal_video"
          ) && (
            <Box mb="xl">
              <Title
                order={3}
                c="#F0F0F0"
                mb="4px"
                style={{ fontFamily: "'Big Shoulders Display', sans-serif", fontWeight: 600 }}
              >
                Videos
              </Title>
              <Stack gap="md">
                {currentTerm.multimedia
                  .map((id) =>
                    multimediaReferences.find((ref) => ref.id === id)
                  )
                  .filter((ref) => ref?.type === "internal_video")
                  .map((ref) => (
                    <Card
                      key={ref?.id}
                      padding="lg"
                      radius="md"
                      style={{
                        background: "rgba(255, 255, 255, 0.05)",
                        border: "1px solid rgba(255, 255, 255, 0.1)",
                        cursor: "pointer",
                        transition: "all 0.3s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-2px)";
                        e.currentTarget.style.borderColor =
                          "rgba(255, 152, 0, 0.5)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.borderColor =
                          "rgba(255, 255, 255, 0.1)";
                      }}
                    >
                      <Text
                        size="md"
                        c="#F0F0F0"
                        style={{ fontFamily: "'Big Shoulders Display', sans-serif" }}
                      >
                        📹 {ref?.title}
                      </Text>
                    </Card>
                  ))}
              </Stack>
            </Box>
          )}

          {/* Images */}
          {currentTerm.multimedia.some(
            (id) =>
              multimediaReferences.find((ref) => ref.id === id)?.type ===
              "internal_image"
          ) && (
            <Box mb="xl">
              <Title
                order={3}
                c="#F0F0F0"
                mb="4px"
                style={{ fontFamily: "'Big Shoulders Display', sans-serif", fontWeight: 600 }}
              >
                Images
              </Title>
              <Stack gap="md">
                {currentTerm.multimedia
                  .map((id) =>
                    multimediaReferences.find((ref) => ref.id === id)
                  )
                  .filter((ref) => ref?.type === "internal_image")
                  .map((ref) => (
                    <Card
                      key={ref?.id}
                      padding="lg"
                      radius="md"
                      style={{
                        background: "rgba(255, 255, 255, 0.05)",
                        border: "1px solid rgba(255, 255, 255, 0.1)",
                        cursor: "pointer",
                        transition: "all 0.3s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-2px)";
                        e.currentTarget.style.borderColor =
                          "rgba(255, 152, 0, 0.5)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.borderColor =
                          "rgba(255, 255, 255, 0.1)";
                      }}
                    >
                      <Text
                        size="md"
                        c="#F0F0F0"
                        style={{ fontFamily: "'Big Shoulders Display', sans-serif" }}
                      >
                        🖼️ {ref?.title}
                      </Text>
                    </Card>
                  ))}
              </Stack>
            </Box>
          )}

          {/* External Videos */}
          {currentTerm.multimedia.some(
            (id) =>
              multimediaReferences.find((ref) => ref.id === id)?.type ===
              "external_video"
          ) && (
            <Box mb="xl">
              <Title
                order={3}
                c="#F0F0F0"
                mb="4px"
                style={{ fontFamily: "'Big Shoulders Display', sans-serif", fontWeight: 600 }}
              >
                External Videos
              </Title>
              <Stack gap="md">
                {currentTerm.multimedia
                  .map((id) =>
                    multimediaReferences.find((ref) => ref.id === id)
                  )
                  .filter((ref) => ref?.type === "external_video")
                  .map((ref) => (
                    <Card
                      key={ref?.id}
                      padding="lg"
                      radius="md"
                      style={{
                        background: "rgba(255, 255, 255, 0.05)",
                        border: "1px solid rgba(255, 255, 255, 0.1)",
                        cursor: "pointer",
                        transition: "all 0.3s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-2px)";
                        e.currentTarget.style.borderColor =
                          "rgba(255, 152, 0, 0.5)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.borderColor =
                          "rgba(255, 255, 255, 0.1)";
                      }}
                    >
                      <Group justify="space-between">
                        <Text
                          size="md"
                          c="#F0F0F0"
                          style={{ fontFamily: "'Big Shoulders Display', sans-serif" }}
                        >
                          🎬 {ref?.title}
                        </Text>
                        <ActionIcon variant="subtle" color="orange">
                          <IconExternalLink size={16} />
                        </ActionIcon>
                      </Group>
                    </Card>
                  ))}
              </Stack>
            </Box>
          )}

          {/* External Links */}
          {currentTerm.multimedia.some(
            (id) =>
              multimediaReferences.find((ref) => ref.id === id)?.type ===
              "external_link"
          ) && (
            <Box mb="xl">
              <Title
                order={3}
                c="#F0F0F0"
                mb="4px"
                style={{ fontFamily: "'Big Shoulders Display', sans-serif", fontWeight: 600 }}
              >
                External Links
              </Title>
              <Stack gap="md">
                {currentTerm.multimedia
                  .map((id) =>
                    multimediaReferences.find((ref) => ref.id === id)
                  )
                  .filter((ref) => ref?.type === "external_link")
                  .map((ref) => (
                    <Card
                      key={ref?.id}
                      padding="lg"
                      radius="md"
                      style={{
                        background: "rgba(255, 255, 255, 0.05)",
                        border: "1px solid rgba(255, 255, 255, 0.1)",
                        cursor: "pointer",
                        transition: "all 0.3s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-2px)";
                        e.currentTarget.style.borderColor =
                          "rgba(255, 152, 0, 0.5)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.borderColor =
                          "rgba(255, 255, 255, 0.1)";
                      }}
                    >
                      <Group justify="space-between">
                        <Text
                          size="md"
                          c="#F0F0F0"
                          style={{ fontFamily: "'Big Shoulders Display', sans-serif" }}
                        >
                          🔗 {ref?.title}
                        </Text>
                        <ActionIcon variant="subtle" color="orange">
                          <IconExternalLink size={16} />
                        </ActionIcon>
                      </Group>
                    </Card>
                  ))}
              </Stack>
            </Box>
          )}

          {/* Podcasts */}
          {currentTerm.multimedia.some(
            (id) =>
              multimediaReferences.find((ref) => ref.id === id)?.type ===
              "podcast"
          ) && (
            <Box mb="xl">
              <Title
                order={3}
                c="#F0F0F0"
                mb="4px"
                style={{ fontFamily: "'Big Shoulders Display', sans-serif", fontWeight: 600 }}
              >
                Podcasts
              </Title>
              <Stack gap="md">
                {currentTerm.multimedia
                  .map((id) =>
                    multimediaReferences.find((ref) => ref.id === id)
                  )
                  .filter((ref) => ref?.type === "podcast")
                  .map((ref) => (
                    <Card
                      key={ref?.id}
                      padding="lg"
                      radius="md"
                      style={{
                        background: "rgba(255, 255, 255, 0.05)",
                        border: "1px solid rgba(255, 255, 255, 0.1)",
                        cursor: "pointer",
                        transition: "all 0.3s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-2px)";
                        e.currentTarget.style.borderColor =
                          "rgba(255, 152, 0, 0.5)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.borderColor =
                          "rgba(255, 255, 255, 0.1)";
                      }}
                    >
                      <Group justify="space-between">
                        <Text
                          size="md"
                          c="#F0F0F0"
                          style={{ fontFamily: "'Big Shoulders Display', sans-serif" }}
                        >
                          🎧 {ref?.title}
                        </Text>
                        <ActionIcon variant="subtle" color="orange">
                          <IconExternalLink size={16} />
                        </ActionIcon>
                      </Group>
                    </Card>
                  ))}
              </Stack>
            </Box>
          )}

          {/* See Also */}
          {currentTerm.seeAlso.length > 0 && (
            <Box>
              <Title
                order={3}
                c="#F0F0F0"
                mb="4px"
                style={{ fontFamily: "'Big Shoulders Display', sans-serif", fontWeight: 600 }}
              >
                See Also
              </Title>
              <Group gap="md">
                {currentTerm.seeAlso
                  .map((id) =>
                    allDictionaryTerms.find((term) => term.id === id)
                  )
                  .filter(Boolean)
                  .map((relatedTerm) =>
                    relatedTerm ? (
                      <L2KBadge
                        key={relatedTerm.id}
                        variant="outline"
                        size="lg"
                        style={{
                          fontFamily: "'Big Shoulders Display', sans-serif",
                          cursor: "pointer",
                          fontSize: "14px",
                          transition: "all 0.3s ease",
                          textTransform: "capitalize",
                        }}
                        onClick={() => handleSeeAlsoClick(relatedTerm.id)}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor =
                            "rgba(255, 152, 0, 0.2)";
                          e.currentTarget.style.transform = "translateY(-1px)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "transparent";
                          e.currentTarget.style.transform = "translateY(0)";
                        }}
                      >
                        {relatedTerm.name}
                      </L2KBadge>
                    ) : null
                  )}
              </Group>
            </Box>
          )}
        </Box>
      </Stack>

      {/* Preview Drawer */}
      <Drawer
        opened={previewDrawerOpened}
        onClose={togglePreviewDrawer}
        title={
          selectedPreviewTerm && (
            <Group justify="space-between" style={{ width: "100%" }}>
              <Title
                order={3}
                style={{ fontFamily: "'Big Shoulders Display', sans-serif", color: "#F0F0F0" }}
              >
                {selectedPreviewTerm.name}
              </Title>
              <Button
                variant="outline"
                size="sm"
                color="orange"
                leftSection={<IconExternalLink size={16} />}
                style={{ fontFamily: "'Big Shoulders Display', sans-serif" }}
                onClick={handleViewFullWord}
              >
                View Full Word
              </Button>
            </Group>
          )
        }
        position="right"
        size="lg"
        styles={{
          content: {
            background: "rgba(23, 23, 23, 0.95)",
            backdropFilter: "blur(15px)",
            border: "1px solid rgba(255, 152, 0, 0.3)",
          },
          header: {
            backgroundColor: "transparent",
            borderBottom: "1px solid rgba(255, 152, 0, 0.3)",
          },
          title: {
            fontFamily: "'Big Shoulders Display', sans-serif",
            color: "#F0F0F0",
          },
        }}
      >
        {selectedPreviewTerm && (
          <ScrollArea style={{ height: "100vh" }}>
            <Stack gap="lg" p="md">
              {/* Definition */}
              <Box>
                <Group gap="sm" mb="md">
                  {selectedPreviewTerm.danceStyles.map((styleId) => (
                    <L2KBadge
                      key={styleId}
                      variant="subtle"
                      style={{ fontFamily: "'Big Shoulders Display', sans-serif" }}
                    >
                      {getDanceStyleName(styleId)}
                    </L2KBadge>
                  ))}
                  <L2KBadge
                    variant="muted"
                    style={{ fontFamily: "'Big Shoulders Display', sans-serif" }}
                  >
                    {selectedPreviewTerm.category}
                  </L2KBadge>
                </Group>
                <Text
                  size="md"
                  c="#F0F0F0"
                  style={{
                    fontFamily: "'Big Shoulders Display', sans-serif",
                    lineHeight: 1.6,
                  }}
                >
                  {selectedPreviewTerm.definition}
                </Text>
              </Box>

              {/* Multimedia sections would go here - same as the current drawer implementation */}

              {/* See Also */}
              {selectedPreviewTerm.seeAlso.length > 0 && (
                <Box>
                  <Title
                    order={4}
                    c="#F0F0F0"
                    mb="xl"
                    style={{ fontFamily: "'Big Shoulders Display', sans-serif" }}
                  >
                    See Also
                  </Title>
                  <Group gap="sm">
                    {selectedPreviewTerm.seeAlso
                      .map((id) =>
                        allDictionaryTerms.find((term) => term.id === id)
                      )
                      .filter(Boolean)
                      .map((relatedTerm) =>
                        relatedTerm ? (
                          <Button
                            key={relatedTerm.id}
                            variant="outline"
                            color="orange"
                            size="sm"
                            onClick={() => {
                              setSelectedPreviewTerm(relatedTerm);
                            }}
                            style={{
                              fontFamily: "'Big Shoulders Display', sans-serif",
                            }}
                          >
                            {relatedTerm.name}
                          </Button>
                        ) : null
                      )}
                  </Group>
                </Box>
              )}
            </Stack>
          </ScrollArea>
        )}
      </Drawer>
    </Box>
  );
}
